/**
 * TermsConditionsContext
 * Manages T&C acceptance state and provides T&C-related functionality to the app
 */

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useTCStatus, useCurrentTC, useAcceptTC, useAcceptanceInfo } from '../hooks/queries/useTermsConditions';
import { useAuth } from './AuthContext';
import { TCContent, AcceptanceInfo } from '../types/termsConditions';

interface TermsConditionsContextType {
  /** Whether the user needs to accept T&C before using the app */
  requiresAcceptance: boolean;
  /** Whether T&C status is being loaded */
  isLoading: boolean;
  /** Current T&C content (null if not loaded) */
  currentTC: TCContent | null;
  /** User's acceptance information (null if not accepted) */
  acceptanceInfo: AcceptanceInfo | null;
  /** Accept the current T&C */
  acceptTerms: () => Promise<void>;
  /** Refresh T&C status */
  refreshStatus: () => void;
  /** Error message if any */
  error: string | null;
  /** Whether acceptance is in progress */
  isAccepting: boolean;
}

const TermsConditionsContext = createContext<TermsConditionsContextType | undefined>(undefined);

interface TermsConditionsProviderProps {
  children: ReactNode;
}

export const TermsConditionsProvider: React.FC<TermsConditionsProviderProps> = ({ children }) => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Only fetch T&C status when user is authenticated
  const {
    data: tcStatus,
    isLoading: statusLoading,
    refetch: refetchStatus,
    error: statusError,
  } = useTCStatus({
    enabled: isAuthenticated && !authLoading,
  });

  // Fetch current T&C content when authenticated (for offline caching)
  // Always fetch when authenticated to ensure T&C is cached for offline use
  const {
    data: currentTC,
    isLoading: tcLoading,
    error: tcError,
  } = useCurrentTC({
    enabled: isAuthenticated && !authLoading,
  });

  // Fetch acceptance info for viewing in settings (and for offline caching)
  // Always fetch when authenticated to ensure acceptance info is cached for offline use
  const {
    data: acceptanceInfo,
    isLoading: acceptanceLoading,
  } = useAcceptanceInfo({
    enabled: isAuthenticated && !authLoading,
  });

  // Accept T&C mutation
  const acceptMutation = useAcceptTC();

  // Handle errors - preserve original error messages for better offline UX
  useEffect(() => {
    if (statusError) {
      console.error('Error fetching T&C status:', statusError);
      // Use the actual error message for offline-specific errors
      const message = statusError.message?.includes('offline')
        ? statusError.message
        : 'Failed to check Terms and Conditions status';
      setError(message);
    } else if (tcError) {
      console.error('Error fetching T&C content:', tcError);
      // Use the actual error message for offline-specific errors
      const message = tcError.message?.includes('offline')
        ? tcError.message
        : 'Failed to load Terms and Conditions';
      setError(message);
    } else {
      setError(null);
    }
  }, [statusError, tcError]);

  const acceptTerms = useCallback(async () => {
    if (!currentTC?.tcId) {
      setError('No Terms and Conditions to accept');
      throw new Error('No Terms and Conditions to accept');
    }

    try {
      setError(null);
      await acceptMutation.mutateAsync(currentTC.tcId);
    } catch (err) {
      console.error('Failed to accept Terms and Conditions:', err);
      setError('Failed to accept Terms and Conditions. Please try again.');
      throw err;
    }
  }, [currentTC?.tcId, acceptMutation]);

  const refreshStatus = useCallback(() => {
    refetchStatus();
  }, [refetchStatus]);

  // Determine if acceptance is required
  // Don't require acceptance if: not authenticated, still loading, or already accepted
  const requiresAcceptance = Boolean(
    isAuthenticated &&
    !authLoading &&
    !statusLoading &&
    tcStatus?.requiresAcceptance === true
  );

  // Determine loading state
  const isLoading = authLoading || statusLoading || (requiresAcceptance && tcLoading);

  const value: TermsConditionsContextType = {
    requiresAcceptance,
    isLoading,
    currentTC: currentTC ?? null,
    acceptanceInfo: acceptanceInfo ?? null,
    acceptTerms,
    refreshStatus,
    error,
    isAccepting: acceptMutation.isPending,
  };

  return (
    <TermsConditionsContext.Provider value={value}>
      {children}
    </TermsConditionsContext.Provider>
  );
};

/**
 * Hook to access T&C context
 */
export const useTermsConditions = (): TermsConditionsContextType => {
  const context = useContext(TermsConditionsContext);
  if (context === undefined) {
    throw new Error('useTermsConditions must be used within a TermsConditionsProvider');
  }
  return context;
};
